import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Projects", href: "#projects" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50 pt-4 px-4 md:px-6"
    >
      <nav
        className={`max-w-[1400px] mx-auto flex items-center justify-between rounded-full px-5 md:px-7 py-3 transition-colors duration-500 ${
          scrolled ? "glass" : "bg-transparent border border-transparent"
        }`}
      >
        <a
          href="#top"
          data-testid="navbar-logo"
          className="flex items-center group"
        >
          <img
            src="/dmab-logo-dark.png"
            alt="DMAB Solutions"
            className="h-11 w-auto transition-transform duration-300 group-hover:scale-[1.03]"
          />
        </a>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm uppercase tracking-[0.15em] text-[#94A3B8] hover:text-white transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </div>

        <a
          href="#contact"
          data-testid="navbar-cta"
          className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/15 px-5 py-2.5 text-sm font-medium text-white hover:bg-[#00F0FF] hover:text-[#050A15] hover:border-[#00F0FF] transition-all duration-300"
        >
          Get in touch <ArrowUpRight size={16} />
        </a>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white p-1"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden glass mt-3 rounded-2xl p-6 flex flex-col gap-5 max-w-[1400px] mx-auto"
          >
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className="text-base uppercase tracking-[0.15em] text-[#94A3B8] hover:text-white transition-colors"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#00F0FF] text-[#050A15] px-5 py-3 font-semibold"
            >
              Get in touch <ArrowUpRight size={16} />
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
